import { Reference } from '@approvalmax/types';
import { ColumnDefinition, Select } from '@approvalmax/ui/src/components';
import sortBy from 'lodash/sortBy';
import uniqueId from 'lodash/uniqueId';
import { selectors } from 'modules/common';
import { backend } from 'modules/data';
import { invalidateFields } from 'modules/integration/actions';
import { useSelector } from 'modules/react-redux';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { notificationService } from 'services/notification';
import { customFieldLevels, CustomFieldType } from 'shared/constants';
import { useUpdateNetSuiteFields } from 'shared/data/webApp/v1';

import { NetSuiteCustomFieldReferenceItem } from '../../types';
import { FieldMenuCell, ReadOnlyCell, SelectCell } from '..';
import { initialCustomField, netSuiteCustomFieldColumnData } from './NetSuiteCustomFields.constants';
import {
    getAccessLevelValue,
    getAvailableIntegrationCodes,
    getWorkflowAccessLevelType,
    isCorrectCustomFieldType,
    mapFieldWorkflow,
    mapLevelToBackendValue,
    showFieldUsedInNotification,
    validateFields,
} from './NetSuiteCustomFields.helpers';
import { messages } from './NetSuiteCustomFields.messages';
import { CustomFieldsStateItem, SaveOptions, UseCustomFieldsTableOptions } from './NetSuiteCustomFields.types';

export const useNetSuiteCustomFieldsMutation = (companyId: string) => {
    const dispatch = useDispatch();

    return useUpdateNetSuiteFields({
        onSuccess: () => {
            notificationService.showInfoToast(messages.updateSuccessText);

            dispatch(invalidateFields({ companyId }));
        },
    });
};

export const useCustomFieldsTable = (options: UseCustomFieldsTableOptions) => {
    const { type, initializer, availableFields, company, showErrors } = options;

    const [customFieldsState, setCustomFieldsState] = useState<CustomFieldsStateItem[]>(initializer);

    const availableIntegrationCodes = useMemo(
        () => getAvailableIntegrationCodes(company.betaFeatures),
        [company.betaFeatures]
    );

    const onCustomFieldAdd = useCallback(() => {
        setCustomFieldsState((current) => [
            ...current,
            {
                id: uniqueId(`netsuiteCustomField-`),
                ...initialCustomField,
            },
        ]);
    }, []);

    const onCustomFieldRemove = useCallback((id: string) => {
        setCustomFieldsState((current) =>
            current.filter((field) => {
                if (field.id === id) {
                    if (field.usedInWorkflows?.length) {
                        showFieldUsedInNotification(field.usedInWorkflows, field.referenceValue?.fieldName);

                        return true;
                    }

                    return false;
                }

                return true;
            })
        );
    }, []);

    const onCustomFieldReorder = useCallback((reorderedRows: CustomFieldsStateItem[]) => {
        setCustomFieldsState(reorderedRows);
    }, []);

    const filteredAvailableFields = useMemo(() => {
        const filteredFields =
            availableFields?.filter(
                (referenceItem) =>
                    isCorrectCustomFieldType(type, referenceItem.fieldScriptId) &&
                    customFieldsState.findIndex((stateItem) => stateItem.referenceValue?.id === referenceItem.id) === -1
            ) ?? [];

        return sortBy(filteredFields, (field) => field.fieldName);
    }, [customFieldsState, availableFields, type]);

    const tableDef = useMemo<ColumnDefinition<CustomFieldsStateItem>[]>(() => {
        const columnDefinitions: ColumnDefinition<CustomFieldsStateItem>[] = [
            {
                id: 'nsFieldName',
                name: messages.nsFieldNameCol,
                width: 250,
                cellComponent: (props) => {
                    const li = props.item;

                    return (
                        <SelectCell {...props} invalid={showErrors && !li.referenceValue}>
                            <Select
                                size='small'
                                spacing='12 8'
                                bordered={false}
                                filterSelectedItems
                                clearable={false}
                                placeholder={messages.nsFieldNamePlaceholder}
                                itemNameKey='fieldName'
                                items={[...(li.referenceValue ? [li.referenceValue] : []), ...filteredAvailableFields]}
                                value={li.referenceValue?.id}
                                onChange={(referenceValueId?: NetSuiteCustomFieldReferenceItem['id']) => {
                                    const value = filteredAvailableFields.find((item) => item.id === referenceValueId);

                                    setCustomFieldsState((current) =>
                                        current.map((item) => {
                                            if (item.id === li.id) {
                                                return {
                                                    ...li,
                                                    referenceValue: value ?? null,
                                                    approvalmaxFieldName: value?.fieldName ?? '',
                                                };
                                            }

                                            return item;
                                        })
                                    );
                                }}
                            />
                        </SelectCell>
                    );
                },
            },
            ...(type === CustomFieldType.Segment
                ? [
                      {
                          id: 'nsFieldLevel',
                          name: messages.fieldLevelCol,
                          width: 160,
                          cellComponent: (props) => {
                              const li = props.item;
                              const readonly = !li.referenceValue;

                              if (readonly) {
                                  return <ReadOnlyCell {...props} text={messages.fieldLevelColPlaceholder} />;
                              }

                              return (
                                  <SelectCell {...props} invalid={showErrors && !li.referenceValue?.level}>
                                      <Select
                                          size='small'
                                          spacing='12 8'
                                          bordered={false}
                                          placeholder={messages.fieldLevelColPlaceholder}
                                          clearable={false}
                                          itemNameKey='text'
                                          items={customFieldLevels}
                                          value={li.referenceValue?.level}
                                          onChange={(valueId?: Reference['id']) => {
                                              setCustomFieldsState((current) =>
                                                  current.map((item) => {
                                                      if (item.id === li.id) {
                                                          return {
                                                              ...item,
                                                              referenceValue: item.referenceValue
                                                                  ? {
                                                                        ...item.referenceValue,
                                                                        level: valueId,
                                                                    }
                                                                  : null,
                                                          };
                                                      }

                                                      return item;
                                                  })
                                              );
                                          }}
                                      />
                                  </SelectCell>
                              );
                          },
                      } as ColumnDefinition<CustomFieldsStateItem>,
                  ]
                : []),
            {
                id: 'fieldInternalId',
                name: messages.fieldInternalIdCol,
                cellComponent: (props) => <ReadOnlyCell {...props} text={props.item.referenceValue?.id || ''} />,
            },
            {
                id: 'fieldScriptId',
                name: messages.fieldScriptIdCol,
                cellComponent: (props) => (
                    <ReadOnlyCell {...props} text={props.item.referenceValue?.fieldScriptId || ''} />
                ),
            },
            {
                id: 'fieldType',
                name: messages.fieldTypeCol,
                cellComponent: (props) => <ReadOnlyCell {...props} text={props.item.referenceValue?.type || ''} />,
            },
            {
                id: 'referenceRecordType',
                name: messages.referenceRecordTypeCol,
                cellComponent: (props) => (
                    <ReadOnlyCell {...props} text={props.item.referenceValue?.referenceRecordType?.name || ''} />
                ),
            },
            ...availableIntegrationCodes.map((integrationCode) => {
                const { columnId: id, name, accessLevelType } = netSuiteCustomFieldColumnData[integrationCode];

                return {
                    id,
                    name,
                    cellComponent: SelectCell,
                    value: getAccessLevelValue(accessLevelType, integrationCode, setCustomFieldsState),
                };
            }),
        ];

        columnDefinitions.push({
            id: 'menu',
            cellComponent: (props) => <FieldMenuCell {...props} onCustomFieldRemove={onCustomFieldRemove} />,
            spacing: '0' as const,
            textAlign: 'center',
            width: 40,
            sticky: 'right',
        });

        return columnDefinitions;
    }, [type, availableIntegrationCodes, showErrors, filteredAvailableFields, onCustomFieldRemove]);

    return {
        customFieldsState,
        onCustomFieldAdd,
        onCustomFieldReorder,
        tableDef,
    };
};

export const useSave = (options: SaveOptions) => {
    const { company, bodyFieldsState, lineFieldsState, segmentFieldsState, setShowErrors } = options;

    const companyTemplates = useSelector((state) => selectors.template.getTemplatesByCompanyId(state, company.id));
    const { mutate: updateNetSuiteFields, isLoading: areFieldsUpdating } = useNetSuiteCustomFieldsMutation(company.id);

    const availableIntegrationCodes = useMemo(
        () => getAvailableIntegrationCodes(company.betaFeatures),
        [company.betaFeatures]
    );

    const onSave = useCallback(() => {
        const areFieldsValid =
            validateFields(bodyFieldsState) && validateFields(lineFieldsState) && validateFields(segmentFieldsState);

        if (!areFieldsValid) {
            setShowErrors(true);

            notificationService.showErrorToast(messages.mandatoryFieldsError);

            return;
        }

        const fields: backend.transfers.AddOrUpdateNetSuiteFieldsTransfer['fields'] = [
            ...segmentFieldsState,
            ...bodyFieldsState,
            ...lineFieldsState,
        ].map((item, index) => {
            const fieldWorkflows = availableIntegrationCodes
                .map((integrationCode) => {
                    const workflow = companyTemplates.find((template) => template.integrationCode === integrationCode);

                    if (!workflow) {
                        return null;
                    }

                    const workflowAccessLevelType = getWorkflowAccessLevelType(integrationCode);

                    return mapFieldWorkflow(workflow.id, item, workflowAccessLevelType);
                })
                .filter((workflow) => workflow !== null);

            return {
                fieldId: item.originalId || undefined,
                name: item.approvalmaxFieldName || item.referenceValue?.fieldName || '',
                fieldType: backend.FieldType.ExactValueRange,
                ExactValues: [],
                netSuiteField: {
                    field: {
                        scriptId: item.referenceValue?.fieldScriptId ?? '',
                        id: item.referenceValue?.id ?? '',
                        name: item.referenceValue?.fieldName ?? '',
                    },
                    level: mapLevelToBackendValue(item.referenceValue?.level),
                    referenceRecordType: item.referenceValue?.referenceRecordType ?? null,
                    order: index,
                    type: item.referenceValue?.type ?? '',
                },
                fieldWorkflows,
            };
        });

        updateNetSuiteFields({ body: { companyId: company.id, fields } });
    }, [
        bodyFieldsState,
        lineFieldsState,
        segmentFieldsState,
        companyTemplates,
        updateNetSuiteFields,
        company.id,
        setShowErrors,
        availableIntegrationCodes,
    ]);

    return {
        onSave,
        areFieldsUpdating,
    };
};
